import React, { useEffect, useRef, useState } from 'react';
import { Box, SxProps, Theme } from "@mui/material";
import LogoPoster from "../../shared/img/rank5ioLogo.webp";
import type { LogoSceneHandle } from "./LogoScene";

interface Props {
    /** Overrides the default front-page sizing. */
    sx?: SxProps<Theme>;
    /**
     * Holding the logo until the balls hit super speed throws a party (rainbows + disco
     * lights over the page): `full` on the front page, `subtle` everywhere else.
     */
    party?: "full" | "subtle";
}

// Extra canvas around the layout box (fraction of its size per side), so tilting and the
// orbiting balls never clip at the canvas edge.
const BLEED = { x: 0.08, y: 0.3 };

const DEFAULT_SIZE = {
    height: { lg: "30vh", md: "30vh", xs: "20vh" },
    width: { lg: "40vw", md: "50vw", xs: "70vw" },
    marginTop: { lg: "5vh", md: "3vh", xs: "4vh" },
};

/**
 * The rank5.io logo. A still poster shows immediately; the interactive Three.js version
 * loads lazily in its own chunk and fades in over it. The 5's colour segments carousel
 * along the stroke; when shown big, hover / tap rolls each segment into a ball that orbits
 * the gap, and holding it down spins the balls up to super speed.
 */
export default function FrontPageLogo({ sx, party = "subtle" }: Props) {
    const boxRef = useRef<HTMLDivElement>(null);
    const sceneRef = useRef<HTMLDivElement>(null);
    const partyRef = useRef(party);
    partyRef.current = party;
    const [live, setLive] = useState(false);

    useEffect(() => {
        const mount = sceneRef.current;
        const hitArea = boxRef.current;
        if (!mount || !hitArea) return;
        let handle: LogoSceneHandle | undefined;
        let cancelled = false;
        import("./LogoScene")
            .then(({ createLogoScene }) => {
                if (cancelled) return;
                handle = createLogoScene({
                    mount,
                    hitArea,
                    bleed: BLEED,
                    onFirstFrame: () => setLive(true),
                    // the party lasts exactly as long as the logo is held
                    onSuperSpeed: () => {
                        import("./logoParty").then(({ startLogoParty }) => startLogoParty(hitArea, partyRef.current));
                    },
                    onSuperSpeedEnd: () => {
                        import("./logoParty").then(({ stopLogoParty }) => stopLogoParty());
                    },
                });
            })
            .catch(() => {
                // No WebGL (or the chunk failed to load): the poster stays.
            });
        return () => {
            cancelled = true;
            handle?.dispose();
        };
    }, []);

    return (
        <Box
            ref={boxRef}
            role="img"
            aria-label="rank5.io online party game"
            sx={[
                {
                    position: "relative",
                    userSelect: "none",
                    WebkitUserSelect: "none",
                    WebkitTouchCallout: "none",
                    WebkitTapHighlightColor: "transparent",
                    touchAction: "manipulation",
                },
                // Default front-page sizing only when the page gives none: its breakpoint
                // rules would otherwise override a caller's plain values on larger screens.
                ...(sx === undefined ? [DEFAULT_SIZE] : Array.isArray(sx) ? sx : [sx]),
            ]}
        >
            <img
                src={LogoPoster}
                alt=""
                aria-hidden
                draggable={false}
                width={1200}
                height={323}
                style={{
                    position: "absolute",
                    inset: 0,
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                    opacity: live ? 0 : 1,
                    transition: "opacity 0.3s",
                    pointerEvents: "none",
                }}
            />
            <div
                ref={sceneRef}
                style={{
                    position: "absolute",
                    top: `${-BLEED.y * 100}%`,
                    bottom: `${-BLEED.y * 100}%`,
                    left: `${-BLEED.x * 100}%`,
                    right: `${-BLEED.x * 100}%`,
                    opacity: live ? 1 : 0,
                    transition: "opacity 0.3s",
                    pointerEvents: "none",
                }}
            />
        </Box>
    );
}
